import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudyDetailPage() {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [relatedStudies, setRelatedStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studyRes, allStudiesRes] = await Promise.all([
          axios.get(`${API}/case-studies/${id}`),
          axios.get(`${API}/case-studies`)
        ]);
        setCaseStudy(studyRes.data);
        
        // Get related studies from same category
        const related = allStudiesRes.data
          .filter(s => s.category === studyRes.data.category && s.id !== id)
          .slice(0, 2);
        setRelatedStudies(related);
      } catch (error) {
        console.error('Error fetching case study:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen section-light">
        <div className="container-custom py-20">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-32 mb-8" />
            <div className="h-12 bg-slate-200 rounded w-3/4 mb-4" />
            <div className="h-6 bg-slate-200 rounded w-1/2 mb-12" />
            <div className="aspect-video bg-slate-200 rounded mb-12" />
          </div>
        </div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="pt-20 min-h-screen section-light">
        <div className="container-custom py-20 text-center">
          <h1 className="heading-lg text-slate-900 mb-4 font-heading">Case Study Not Found</h1>
          <Link to="/case-studies">
            <Button className="bg-oracle-red hover:bg-oracle-red-dark text-white">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Case Studies
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="case-study-detail-page">
      {/* Hero Section */}
      <section className="section-dark py-20 md:py-32">
        <div className="container-custom">
          <Link 
            to="/case-studies"
            className="inline-flex items-center text-slate-400 hover:text-white font-body text-sm mb-8 transition-colors"
            data-testid="back-to-case-studies"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Case Studies
          </Link>
          
          <div className="max-w-4xl">
            <span className="eyebrow mb-4 inline-block">{caseStudy.category.toUpperCase()} • {caseStudy.industry}</span>
            <h1 className="heading-xl text-white mb-6 font-heading">
              {caseStudy.title}
            </h1>
            <p className="text-slate-300 font-body text-lg md:text-xl">
              {caseStudy.client}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="section-light">
        <div className="container-custom -mt-8">
          <div className="aspect-video rounded-sm overflow-hidden shadow-2xl">
            <img 
              src={caseStudy.image_url || 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200'}
              alt={caseStudy.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="section-light py-12 border-b border-slate-200" data-testid="case-study-metrics">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {caseStudy.metrics?.map((metric, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-white rounded-sm border border-slate-200"
              >
                <div className="font-heading text-3xl md:text-4xl font-bold text-oracle-red mb-2">
                  {metric.value}
                </div>
                <div className="font-body text-sm text-slate-600">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-light py-20 md:py-32" data-testid="case-study-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Challenge */}
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                  The Challenge
                </h2>
                <p className="font-body text-slate-700 text-base leading-relaxed">
                  {caseStudy.challenge}
                </p>
              </div>

              {/* Solution */}
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                  Our Solution
                </h2>
                <p className="font-body text-slate-700 text-base leading-relaxed">
                  {caseStudy.solution}
                </p>
              </div>

              {/* Results */}
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                  The Results
                </h2>
                <p className="font-body text-slate-700 text-base leading-relaxed">
                  {caseStudy.results}
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <Card className="card-dark sticky top-28">
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-bold text-white mb-6">
                    Ready for Similar Results?
                  </h3>
                  <p className="font-body text-slate-400 text-sm mb-6">
                    Let's discuss how we can help transform your enterprise.
                  </p>
                  <Link to="/contact">
                    <Button 
                      className="w-full bg-oracle-red hover:bg-oracle-red-dark text-white font-body mb-4"
                      data-testid="case-study-contact-btn"
                    >
                      Schedule Consultation
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button 
                      variant="outline" 
                      className="w-full border-slate-600 text-white hover:bg-slate-800 font-body"
                    >
                      Request Proposal
                    </Button>
                  </Link>
                  
                  <div className="mt-8 pt-8 border-t border-slate-700">
                    <h4 className="font-body text-xs uppercase tracking-wider text-slate-500 mb-4">
                      What We Delivered
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-oracle-red flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-slate-300">
                          Custom {caseStudy.category} solution
                        </span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-oracle-red flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-slate-300">
                          End-to-end implementation
                        </span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-oracle-red flex-shrink-0 mt-0.5" />
                        <span className="font-body text-sm text-slate-300">
                          Ongoing support & optimization
                        </span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Related Case Studies */}
      {relatedStudies.length > 0 && (
        <section className="section-dark py-20 md:py-32" data-testid="related-case-studies">
          <div className="container-custom">
            <h2 className="heading-lg text-white mb-12 font-heading">
              Related Case Studies
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relatedStudies.map((study) => (
                <Link 
                  key={study.id} 
                  to={`/case-studies/${study.id}`}
                  className="group"
                >
                  <Card className="card-dark overflow-hidden h-full">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={study.image_url || 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800'}
                        alt={study.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <span className="eyebrow mb-2 inline-block">{study.category.toUpperCase()}</span>
                      <h3 className="font-heading text-lg font-bold text-white group-hover:text-oracle-red transition-colors">
                        {study.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
